import { Observable } from 'rxjs';
import { CancellationService } from 'src/app/_services/cancellation.service';
import { APIResponse } from 'src/app/_types/api-responses.types';
import {
  CancellationFlowDetailsResponse,
  CancellationFlowRef,
} from 'src/app/_types/cancellation.types';
import { InsuranceFlowSearchPurpose } from 'src/app/_types/insurance-flow.types';
import {
  CancellationFlowStatus,
  isCancellationFlowOver,
  isReplacementReady,
} from 'src/app/enums/cancellation/cancellation-flow-status.enum';
import {
  getInsuranceFlowLabel,
  getInsuranceFlowStatusClass,
} from 'src/app/enums/insurance-flow/insurance-flow-status.enum';

import { CancellationCardVariant } from './cancellation-card-variant';

/** The two flows a cancellation links: the policy it ends, and the one that takes over. */
export type CancellationLinkTarget = 'basePolicy' | 'replacement';

/**
 * Everything that differs between the two link cards. The card itself only
 * knows how to show a variant, open the picker and send the link.
 */
export type CancellationLinkTargetSpec = {
  modalTitle: string;
  modalSubtitle: string;
  pickerLabel: string;
  /** Only affaires holding a policy qualify. */
  withPolicyOnly: boolean;
  /** Which statuses the server offers in the picker. */
  purpose: InsuranceFlowSearchPurpose;
  successMessage: string;
  /** The flow this side names, if any. */
  linked: (
    cancellation: CancellationFlowDetailsResponse
  ) => CancellationFlowRef | null;
  /**
   * The other side. A replacement and the policy it replaces share an affaire,
   * so once one is named the picker stays inside its affaire; the server
   * checks the same rule either way round.
   */
  counterpart: (
    cancellation: CancellationFlowDetailsResponse
  ) => CancellationFlowRef | null;
  variant: (
    cancellation: CancellationFlowDetailsResponse
  ) => CancellationCardVariant;
  link: (
    service: CancellationService,
    cancellationId: string,
    flowId: string
  ) => Observable<APIResponse<CancellationFlowDetailsResponse>>;
};

/**
 * The policy being cancelled. A cancellation opened for a policy held
 * elsewhere has none on our side; once that policy's flow exists, this is
 * where it gets linked.
 */
const basePolicyVariant = (
  cancellation: CancellationFlowDetailsResponse
): CancellationCardVariant => {
  const base = cancellation.baseInsuranceFlow;
  if (!base) {
    return {
      tone: 'neutral',
      title: cancellation.policyNumber
        ? $localize`Police externe · ${cancellation.policyNumber}`
        : $localize`Police externe`,
      pill: $localize`Non liée`,
      pillClass: 'gray',
      actionLabel: isCancellationFlowOver(cancellation.status)
        ? null
        : $localize`Lier la police`,
    };
  }
  return {
    tone:
      cancellation.status === CancellationFlowStatus.CLOSED
        ? 'plain'
        : 'neutral',
    title: $localize`Police à résilier · ${base.displayId}`,
    pill: getInsuranceFlowLabel(base.status),
    pillClass: getInsuranceFlowStatusClass(base.status),
    actionLabel: $localize`Voir l'affaire`,
  };
};

/**
 * The policy that takes over the coverage: not yet named, named and still on
 * its way, ready for the letter to go (in force or about to take effect), or —
 * once the cancellation is over — the one that did.
 */
const replacementVariant = (
  cancellation: CancellationFlowDetailsResponse
): CancellationCardVariant => {
  const replacement = cancellation.replacementInsuranceFlow;
  if (!replacement) {
    return {
      tone: 'neutral',
      title: $localize`Aucune affaire de remplacement liée`,
      pill: $localize`Non liée`,
      pillClass: 'gray',
      actionLabel: isCancellationFlowOver(cancellation.status)
        ? null
        : $localize`Lier une affaire`,
    };
  }
  const pill = getInsuranceFlowLabel(replacement.status);
  const pillClass = getInsuranceFlowStatusClass(replacement.status);
  if (cancellation.status === CancellationFlowStatus.CLOSED) {
    return {
      tone: 'plain',
      title: $localize`Résiliation effective · ${replacement.displayId}`,
      pill,
      pillClass,
      actionLabel: $localize`Voir ${replacement.displayId}`,
    };
  }
  if (isReplacementReady(replacement.status)) {
    return {
      tone: 'success',
      title: $localize`Police de remplacement prête`,
      description: $localize`La résiliation peut désormais être envoyée à l'assureur`,
      pill,
      pillClass: 'success',
      actionLabel: $localize`Voir l'affaire`,
    };
  }
  return {
    tone: 'warning',
    title: $localize`Résiliation en attente · dépend du statut de l’affaire de remplacement ${replacement.displayId}`,
    description: $localize`La lettre pourra être envoyée à l’assureur uniquement lorsque la nouvelle police sera active ou en attente de prise d’effet`,
    actionLabel: $localize`Voir l'affaire`,
  };
};

export const CANCELLATION_LINK_TARGETS: Record<
  CancellationLinkTarget,
  CancellationLinkTargetSpec
> = {
  basePolicy: {
    modalTitle: $localize`Lier la police à résilier`,
    modalSubtitle: $localize`L'affaire LTY de la police décrite dans cette demande.`,
    pickerLabel: $localize`Police à résilier`,
    withPolicyOnly: true,
    purpose: 'BASE_POLICY',
    successMessage: $localize`Police à résilier liée`,
    linked: (cancellation) => cancellation.baseInsuranceFlow,
    counterpart: (cancellation) => cancellation.replacementInsuranceFlow,
    variant: basePolicyVariant,
    link: (service, id, flowId) =>
      service.updateBasePolicy(id, { baseInsuranceFlowId: flowId }),
  },
  replacement: {
    modalTitle: $localize`Lier une affaire de remplacement`,
    modalSubtitle: $localize`La résiliation ne pourra aboutir qu'une fois cette affaire active.`,
    pickerLabel: $localize`Affaire de remplacement`,
    withPolicyOnly: false,
    purpose: 'REPLACEMENT',
    successMessage: $localize`Affaire de remplacement liée`,
    linked: (cancellation) => cancellation.replacementInsuranceFlow,
    counterpart: (cancellation) => cancellation.baseInsuranceFlow,
    variant: replacementVariant,
    link: (service, id, flowId) =>
      service.updateReplacement(id, { replacementInsuranceFlowId: flowId }),
  },
};
